import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dropdown, DropdownOptions, Modal } from '../UI';
import { Button } from '../UI/base/Button';
import type { WeekDateRangeResponse } from '../../api/generated';
import type { WeekRange } from '../../hooks/useWeekRangeCalculation';
import { formatDate } from '../../utils/dateUtils';
import modalStyles from '../UI/Modal.module.css';
import styles from './CalculateWeeksModal.module.css';

interface CalculateWeeksModalProps {
  weeks: WeekDateRangeResponse[];
  // The week both fields open on; falls back to the year's first week when it is not in `weeks`.
  initialWeekNumber: number;
  isTriggering: boolean;
  onConfirm: (range: WeekRange) => void | Promise<void>;
  onClose: () => void;
}

export const CalculateWeeksModal = ({
  weeks,
  initialWeekNumber,
  isTriggering,
  onConfirm,
  onClose
}: CalculateWeeksModalProps) => {
  const { t, i18n } = useTranslation();
  const initialWeek = weeks.find((w) => w.weekNumber === initialWeekNumber) ?? weeks[0];
  const [fromWeek, setFromWeek] = useState<WeekDateRangeResponse>(initialWeek);
  const [toWeek, setToWeek] = useState<WeekDateRangeResponse>(initialWeek);

  // The range cannot run backwards, so the end field only offers weeks from the start onwards.
  const toWeekOptions = useMemo(
    () => weeks.filter((w) => w.weekNumber >= fromWeek.weekNumber),
    [weeks, fromWeek]
  );
  const weekCount = toWeek.weekNumber - fromWeek.weekNumber + 1;

  const handleFromChange = (week: WeekDateRangeResponse): void => {
    setFromWeek(week);
    if (week.weekNumber > toWeek.weekNumber) setToWeek(week);
  };

  const handleConfirm = (): Promise<void> | void =>
    onConfirm({ fromWeekNumber: fromWeek.weekNumber, toWeekNumber: toWeek.weekNumber });

  const weekDisplay = (week: WeekDateRangeResponse) => (
    <>
      {t('gantt-calculate-weeks-week-display', { week: week.weekNumber })}{' '}
      <span className={styles.weekDates}>
        {formatDate(week.startDate, i18n.language)} - {formatDate(week.endDate, i18n.language)}
      </span>
    </>
  );

  const weekField = (
    label: string,
    selected: WeekDateRangeResponse,
    options: WeekDateRangeResponse[],
    onChange: (week: WeekDateRangeResponse) => void
  ) => (
    <Dropdown label={label} selectedOption={weekDisplay(selected)}>
      <DropdownOptions
        options={options}
        selectedOption={selected}
        getKey={(week) => week.weekNumber}
        displayOption={weekDisplay}
        handleChange={onChange}
      />
    </Dropdown>
  );

  return (
    <Modal
      title={t('gantt-calculate-weeks-title')}
      onClose={onClose}
      onConfirm={() => void handleConfirm()}
      width={480}
      footer={
        <div className={modalStyles.footerRow}>
          <Button type="secondary" onClick={onClose} disabled={isTriggering}>
            {t('save-modal-cancel')}
          </Button>
          <Button type="primary" onClick={handleConfirm} disabled={isTriggering}>
            {t('gantt-calculate-weeks-confirm')}
          </Button>
        </div>
      }
    >
      <div className={styles.form}>
        {weekField(t('gantt-calculate-weeks-from-label'), fromWeek, weeks, handleFromChange)}
        {weekField(t('gantt-calculate-weeks-to-label'), toWeek, toWeekOptions, setToWeek)}
        <p className={styles.summary}>{t('gantt-calculate-weeks-summary', { count: weekCount })}</p>
      </div>
    </Modal>
  );
};
